import { useEffect, useRef, useCallback } from 'react';

import { safeSessionStorage } from '../lib';

type UseScrollRestorationOptions = {
  storageKey: string;
  isContentLoaded: boolean;
  maxAttempts?: number;
  attemptInterval?: number;
  timeoutMinutes?: number;
};

/**
 * Hook for restoring scroll position after navigation
 * Handles async content loading with retry mechanism
 */
export const useScrollRestoration = ({
  storageKey,
  isContentLoaded,
  maxAttempts = 10,
  attemptInterval = 200,
  timeoutMinutes = 5,
}: UseScrollRestorationOptions) => {
  const attemptCountRef = useRef(0);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const clearRestoration = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    safeSessionStorage.removeItem(storageKey);
    safeSessionStorage.removeItem('lastScrollTime');
  }, [storageKey]);

  const tryRestoreScroll = useCallback(() => {
    const savedPosition = safeSessionStorage.getItem(storageKey);
    const lastScrollTime = safeSessionStorage.getItem('lastScrollTime');

    if (!savedPosition || !lastScrollTime) {
      return false;
    }

    // Check if position is not too old
    const timeDiff = Date.now() - parseInt(lastScrollTime, 10);
    if (timeDiff > timeoutMinutes * 60 * 1000) {
      clearRestoration();
      return false;
    }

    const targetPosition = parseInt(savedPosition, 10);
    const currentHeight = document.documentElement.scrollHeight;

    // Check if page has enough height or max attempts reached
    if (
      currentHeight > targetPosition ||
      attemptCountRef.current >= maxAttempts
    ) {
      const finalPosition = Math.min(
        targetPosition,
        currentHeight - window.innerHeight,
      );
      window.scrollTo(0, finalPosition);
      clearRestoration();
      return true;
    }

    // Schedule next attempt
    attemptCountRef.current++;
    timeoutIdRef.current = setTimeout(tryRestoreScroll, attemptInterval);
    return false;
  }, [
    storageKey,
    maxAttempts,
    attemptInterval,
    timeoutMinutes,
    clearRestoration,
  ]);

  useEffect(() => {
    if (!isContentLoaded) {
      return;
    }

    // Reset attempt counter
    attemptCountRef.current = 0;

    // Start restoration process with initial delay
    timeoutIdRef.current = setTimeout(tryRestoreScroll, 100);

    // Cleanup on unmount
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [isContentLoaded, tryRestoreScroll]);

  return {
    saveScrollPosition: useCallback(() => {
      const currentScroll = window.pageYOffset || window.scrollY;
      safeSessionStorage.setItem(storageKey, String(currentScroll));
      safeSessionStorage.setItem('lastScrollTime', String(Date.now()));
    }, [storageKey]),
    clearRestoration,
  };
};
